import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useDeleteGroupMutation } from "../../infrastructure/api/membersGroupsApi";
import type { Group } from "../../domain/entities/Member";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: Group | null;
}

export const DeleteGroupDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  group,
}) => {
  const { t } = useTranslation("membersGroups");
  const [deleteGroup, { isLoading }] = useDeleteGroupMutation();

  const handleConfirm = async () => {
    if (!group) return;
    try {
      await deleteGroup(group.id).unwrap();
      toast.success(t("deleteGroupDialog.success", { name: group.name }));
      onOpenChange(false);
    } catch {
      toast.error(t("deleteGroupDialog.error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("deleteGroupDialog.title")}
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            {t("deleteGroupDialog.confirm", { name: group?.name ?? "" })}
          </p>
        </DialogHeader>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="px-4 py-5 text-primary border-primary hover:text-primary hover:bg-white cursor-pointer"
          >
            {t("deleteGroupDialog.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            loading={isLoading}
            className="bg-[#E53935] hover:bg-[#E53935] text-white px-4 py-5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("deleteGroupDialog.delete")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
